import { useState, useEffect, useCallback, useRef } from 'react';
import { reservasApi, clientesApi, mesasApi } from '../services/api';
import { useToast } from '../hooks/useToast.tsx';
import { signalRService } from '../services/signalr';
import { ReservaModal } from '../components/ReservaModal';
import { buildReservaFormFromNow, ensureClienteForReserva, validateReservaForm, type ReservaFormErrors } from '../utils/reservaForm';
import type { Reserva, ReservaListFilter, Cliente, Mesa, CreateReservaRequest } from '../types';
import { Plus, ChevronLeft, ChevronRight, Calendar, Search, X, CheckCircle, XCircle, Edit } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';

export function ReservasPage() {
  const { success, error } = useToast();
  const { isAuthenticated } = useAuth();
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ReservaListFilter>({
    data: undefined,
    apenasHoje: true,
    incluirCanceladas: false,
    incluirFechadas: false,
    page: 1,
    pageSize: 50,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [reservaEditando, setReservaEditando] = useState<Reserva | null>(null);
  const [reservaDetalhe, setReservaDetalhe] = useState<Reserva | null>(null);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [formData, setFormData] = useState<CreateReservaRequest>(() => buildReservaFormFromNow());
  const [formErrors, setFormErrors] = useState<ReservaFormErrors>({});
  const [saving, setSaving] = useState(false);

  const loadReservasRef = useRef<() => Promise<void>>(async () => {});
  const loadReservas = useCallback(async (background = false) => {
    try {
      if (!background) setLoading(true);
      const response = await reservasApi.getAll(filter);
      if (response.success && response.data) {
        setReservas(response.data.items);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages);
      }
    } catch {
      if (!background) error('Erro ao carregar reservas');
    } finally {
      if (!background) setLoading(false);
    }
    loadReservasRef.current = () => loadReservas(true);
  }, [filter, error]);

  const loadAux = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([
        clientesApi.getAll(undefined, 1, 200),
        mesasApi.getAll(),
      ]);
      if (c.success && c.data) setClientes(c.data.items.filter(x => !x.clienteEventual && !x.bloqueado));
      if (m.success && m.data) setMesas(m.data.filter(x => x.podeReservar));
    } catch {
      error('Erro ao carregar dados auxiliares');
    }
  }, [error]);

  useEffect(() => { loadReservas(false); }, [loadReservas]);
  useEffect(() => { loadAux(); }, []);

  // SignalR: reload reservas quando outro dispositivo altera dados — sem recriar subscrição a cada filtro
  const reservasHandlersRef = useRef<Parameters<typeof signalRService.start>[0] | null>(null);
  useEffect(() => {
    if (!isAuthenticated) return;
    const handlers: Parameters<typeof signalRService.start>[0] = {
      onDataChanged: () => loadReservasRef.current?.(),
    };
    reservasHandlersRef.current = handlers;
    signalRService.start(handlers);
    return () => {
      if (reservasHandlersRef.current) signalRService.stop(reservasHandlersRef.current);
    };
  }, [isAuthenticated]);

  const handleNovaReserva = () => {
    setFormData(buildReservaFormFromNow());
    setFormErrors({});
    setReservaEditando(null);
    setShowModal(true);
  };

  const handleEditar = (reserva: Reserva) => {
    setFormData({
      dataDaReserva: reserva.dataDaReserva,
      numeroPessoas: reserva.numeroPessoas,
      contatoReserva: reserva.contatoReserva || '',
      notasReserva: reserva.notasReserva || '',
      nomeCliente: reserva.nomeCliente || '',
      clienteId: reserva.clienteId,
      mesaId: reserva.mesaId,
    });
    setFormErrors({});
    setReservaEditando(reserva);
    setReservaDetalhe(null);
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = validateReservaForm(formData);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      const clienteId = await ensureClienteForReserva({ formData, clientes, onClientesChanged: loadAux });

      if (reservaEditando) {
        const response = await reservasApi.update(reservaEditando.id, {
          ...formData,
          nomeCliente: formData.nomeCliente || undefined,
          clienteId,
          versao: reservaEditando.versao,
        });
        if (response.success) {
          success('Reserva atualizada com sucesso');
          setShowModal(false);
          loadReservas();
        } else {
          error(response.message || 'Erro ao atualizar reserva');
        }
      } else {
        const response = await reservasApi.create({
          ...formData,
          nomeCliente: formData.nomeCliente || undefined,
          clienteId,
        });
        if (response.success) {
          success(clienteId !== formData.clienteId ? 'Cliente criado e reserva guardada' : 'Reserva criada com sucesso');
          setShowModal(false);
          loadReservas();
        } else {
          error(response.message || 'Erro ao criar reserva');
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao guardar reserva';
      error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmar = async (reserva: Reserva) => {
    if (!confirm('Confirmar esta reserva?')) return;
    try {
      const response = await reservasApi.confirmar(reserva.id, { versao: reserva.versao });
      if (response.success) {
        success('Reserva confirmada');
        setShowModal(false);
        setReservaDetalhe(null);
        loadReservas();
      } else {
        error(response.message || 'Erro ao confirmar');
      }
    } catch {
      error('Erro ao confirmar reserva');
    }
  };

  const handleCancelar = async (reserva: Reserva) => {
    if (!confirm('Tem a certeza que deseja cancelar esta reserva?')) return;
    try {
      const response = await reservasApi.cancelar(reserva.id, { versao: reserva.versao });
      if (response.success) {
        success('Reserva cancelada');
        setShowModal(false);
        setReservaDetalhe(null);
        loadReservas();
      } else {
        error(response.message || 'Erro ao cancelar');
      }
    } catch {
      error('Erro ao cancelar reserva');
    }
  };

  const changeDay = (delta: number) => {
    const base = filter.data ? parseISO(filter.data) : new Date();
    const next = new Date(base);
    next.setDate(next.getDate() + delta);
    setFilter(prev => ({ ...prev, data: format(next, 'yyyy-MM-dd'), apenasHoje: false, page: 1 }));
  };

  const setHoje = () => {
    setFilter(prev => ({ ...prev, data: undefined, apenasHoje: true, page: 1 }));
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Reservas</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleNovaReserva}>
            <Plus className="w-4 h-4" />
            Nova Reserva
          </button>
        </div>
      </div>

      <div className="card">
        <div className="reservas-toolbar">
          <div className="date-nav">
            <button className="btn btn-ghost btn-sm" onClick={() => changeDay(-1)} aria-label="Dia anterior">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="current-date">
              <Calendar className="w-4 h-4" aria-hidden="true" />
              {filter.data
                ? format(parseISO(filter.data), 'EEEE, d MMMM yyyy', { locale: pt })
                : 'Hoje'}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={() => changeDay(1)} aria-label="Dia seguinte">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button className="btn btn-secondary btn-sm" onClick={setHoje}>
              Hoje
            </button>
          </div>

          <div className="reservas-toggles">
            <label className="checkbox-label checkbox-sm">
              <input
                type="checkbox"
                checked={filter.incluirCanceladas}
                onChange={e => setFilter(prev => ({ ...prev, incluirCanceladas: e.target.checked, page: 1 }))}
              />
              <span className="checkbox-custom" aria-hidden="true"></span>
              <span>Canceladas</span>
            </label>
            <label className="checkbox-label checkbox-sm">
              <input
                type="checkbox"
                checked={filter.incluirFechadas}
                onChange={e => setFilter(prev => ({ ...prev, incluirFechadas: e.target.checked, page: 1 }))}
              />
              <span className="checkbox-custom" aria-hidden="true"></span>
              <span>Confirmadas</span>
            </label>
          </div>
        </div>

        {loading ? (
          <div className="page-loading">
            <div className="spinner"></div>
            <p>A carregar reservas...</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  <th>Cliente</th>
                  <th>Pessoas</th>
                  <th>Mesa</th>
                  <th>Estado</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {reservas.map(reserva => (
                  <tr key={reserva.id}>
                    <td>{format(parseISO(reserva.dataDaReserva), 'dd/MM/yyyy HH:mm', { locale: pt })}</td>
                    <td>{reserva.nomeCliente || reserva.cliente?.nome || '—'}</td>
                    <td>{reserva.numeroPessoas}</td>
                    <td>{reserva.mesa ? `Mesa ${reserva.mesa.numero}` : 'Sem mesa'}</td>
                    <td>
                      <span className={`badge ${reserva.reservaCancelada ? 'badge-danger' : reserva.reservaFechada ? 'badge-success' : 'badge-warning'}`}>
                        {reserva.reservaCancelada ? 'Cancelada' : reserva.reservaFechada ? 'Confirmada' : 'Pendente'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setReservaDetalhe(reserva)} aria-label="Ver detalhes">
                          <Search className="w-4 h-4" />
                        </button>
                        {!reserva.reservaCancelada && !reserva.reservaFechada && (
                          <>
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleEditar(reserva)} aria-label="Editar">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="btn btn-ghost btn-sm btn-icon text-green-600" onClick={() => handleConfirmar(reserva)} aria-label="Confirmar">
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button className="btn btn-ghost btn-sm btn-icon text-red-600" onClick={() => handleCancelar(reserva)} aria-label="Cancelar">
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && reservas.length === 0 && (
          <div className="empty-state">
            <Calendar className="w-12 h-12 text-gray-300" />
            <h3>Nenhuma reserva encontrada</h3>
            <p>Crie a primeira reserva para começar</p>
            <button className="btn btn-primary mt-4" onClick={handleNovaReserva}>
              <Plus className="w-4 h-4" />
              Nova Reserva
            </button>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="btn btn-secondary btn-sm"
              disabled={filter.page <= 1}
              onClick={() => setFilter(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              Anterior
            </button>
            <span className="pagination-info">
              Página {filter.page} de {totalPages} · {totalCount} reservas
            </span>
            <button
              className="btn btn-secondary btn-sm"
              disabled={filter.page >= totalPages}
              onClick={() => setFilter(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              Seguinte
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <ReservaModal
          title={reservaEditando ? 'Editar Reserva' : 'Nova Reserva'}
          formData={formData}
          formErrors={formErrors}
          clientes={clientes}
          mesas={mesas}
          onClose={() => { setShowModal(false); setReservaEditando(null); }}
          onSubmit={handleSubmit}
          onChange={setFormData}
          loading={saving}
          reservaEditando={reservaEditando}
          onConfirmar={reservaEditando ? handleConfirmar : undefined}
          onCancelar={reservaEditando ? handleCancelar : undefined}
          onEditar={reservaEditando ? handleEditar : undefined}
        />
      )}

      {/* Detalhes */}
      {reservaDetalhe && (
        <div className="modal-overlay" onClick={() => setReservaDetalhe(null)} role="dialog" aria-modal="true" aria-labelledby="details-title">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <header className="modal-header">
              <h2 id="details-title" className="modal-title">Reserva #{reservaDetalhe.id}</h2>
              <button className="modal-close" onClick={() => setReservaDetalhe(null)} aria-label="Fechar">
                <X className="w-5 h-5" />
              </button>
            </header>

            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Cliente</span>
                  <span className="detail-value">{reservaDetalhe.nomeCliente || reservaDetalhe.cliente?.nome || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Telefone</span>
                  <span className="detail-value">{reservaDetalhe.contatoReserva || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Data/Hora</span>
                  <span className="detail-value">{format(parseISO(reservaDetalhe.dataDaReserva), 'dd/MM/yyyy HH:mm', { locale: pt })}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Pessoas</span>
                  <span className="detail-value">{reservaDetalhe.numeroPessoas}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Mesa</span>
                  <span className="detail-value">{reservaDetalhe.mesa ? `Mesa ${reservaDetalhe.mesa.numero}` : 'Sem mesa'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Estado</span>
                  <span className="detail-value">
                    <span className={`badge ${reservaDetalhe.reservaCancelada ? 'badge-danger' : reservaDetalhe.reservaFechada ? 'badge-success' : 'badge-warning'}`}>
                      {reservaDetalhe.reservaCancelada ? 'Cancelada' : reservaDetalhe.reservaFechada ? 'Confirmada' : 'Pendente'}
                    </span>
                  </span>
                </div>
                {reservaDetalhe.notasReserva && (
                  <div className="detail-item detail-full">
                    <span className="detail-label">Notas</span>
                    <span className="detail-value">{reservaDetalhe.notasReserva}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Criado por</span>
                  <span className="detail-value">{reservaDetalhe.criadoPorNome || '—'}</span>
                </div>
                {reservaDetalhe.editadoPorNome && (
                  <div className="detail-item">
                    <span className="detail-label">Editado por</span>
                    <span className="detail-value">{reservaDetalhe.editadoPorNome}</span>
                  </div>
                )}
              </div>
            </div>

            <footer className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setReservaDetalhe(null)}>
                Fechar
              </button>
              {!reservaDetalhe.reservaFechada && !reservaDetalhe.reservaCancelada && (
                <>
                  <button className="btn btn-danger" onClick={() => { handleCancelar(reservaDetalhe); }}>
                    <XCircle className="w-4 h-4" />
                    Cancelar
                  </button>
                  <button className="btn btn-success" onClick={() => { handleConfirmar(reservaDetalhe); }}>
                    <CheckCircle className="w-4 h-4" />
                    Confirmar
                  </button>
                  <button className="btn btn-primary" onClick={() => handleEditar(reservaDetalhe)}>
                    <Edit className="w-4 h-4" />
                    Editar
                  </button>
                </>
              )}
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}